import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  MapPinIcon, 
  StarIcon,
  ViewColumnsIcon,
  ListBulletIcon 
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

interface Seller {
  seller_id: number;
  business_name: string;
  contact_name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  business_type: string;
  profile_type: string;
  verification_status: string;
  rating: number;
  review_count: number;
  image_url?: string;
  specialties: string[];
  inventory_status: string;
  website?: string;
  description?: string;
  social_media?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
}

interface EnhancedSellerDirectoryProps {
  className?: string;
  limit?: number;
  showFilters?: boolean;
  title?: string;
}

const EnhancedSellerDirectory: React.FC<EnhancedSellerDirectoryProps> = ({
  className = '',
  limit,
  showFilters = true,
  title = 'Find Trusted Beekeepers & Suppliers'
}) => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filters, setFilters] = useState({
    search: '',
    businessType: '',
    state: '',
    profileType: '',
    verificationStatus: ''
  });

  // Mock data for development
  const mockSellers: Seller[] = [
    {
      seller_id: 1,
      business_name: "Georgia Honey Farms",
      contact_name: "Sarah Johnson",
      email: "sarah@georgiahoney.com",
      phone: "(404) 555-0123",
      address: "123 Peachtree St",
      city: "Atlanta",
      state: "GA",
      latitude: 33.7490,
      longitude: -84.3880,
      business_type: "Honey Producer",
      profile_type: "premium",
      verification_status: "approved",
      rating: 4.8,
      review_count: 127,
      image_url: "/api/placeholder/300/200",
      specialties: ["Wildflower Honey", "Clover Honey", "Raw Honey"],
      inventory_status: "In Stock",
      website: "https://georgiahoney.com",
      description: "Family-owned operation producing premium wildflower and clover honey since 1985. We focus on sustainable beekeeping practices.",
      social_media: {
        facebook: "georgiahoneyfarms",
        instagram: "georgiahoneyfarms"
      }
    },
    {
      seller_id: 2,
      business_name: "Carolina Queen Bees",
      contact_name: "Mike Wilson",
      email: "mike@carolinaqueen.com",
      phone: "(704) 555-0124",
      address: "456 Queen St",
      city: "Charlotte",
      state: "NC",
      latitude: 35.2271,
      longitude: -80.8431,
      business_type: "Queen Breeder",
      profile_type: "premium",
      verification_status: "approved",
      rating: 4.9,
      review_count: 89,
      image_url: "/api/placeholder/300/200",
      specialties: ["Italian Queens", "Carniolan Queens", "VSH Queens"],
      inventory_status: "Limited Stock",
      website: "https://carolinaqueen.com",
      description: "Breeding high-quality, mite-resistant queen bees for over 20 years. All queens are marked and guaranteed.",
      social_media: {
        facebook: "carolinaqueenbees",
        instagram: "carolinaqueenbees"
      }
    },
    {
      seller_id: 3,
      business_name: "Alabama Bee Supply",
      contact_name: "Janet Davis",
      email: "janet@alabamabee.com",
      phone: "(334) 555-0125",
      address: "789 Bee Ave",
      city: "Montgomery",
      state: "AL",
      latitude: 32.3617,
      longitude: -86.2792,
      business_type: "Equipment Supplier",
      profile_type: "basic",
      verification_status: "pending",
      rating: 4.5,
      review_count: 43,
      image_url: "/api/placeholder/300/200",
      specialties: ["Hive Boxes", "Frames", "Protective Gear"],
      inventory_status: "In Stock",
      description: "Complete beekeeping equipment supplier serving the Southeast. Fast shipping and competitive prices."
    },
    {
      seller_id: 4,
      business_name: "Florida Nuc Specialists",
      contact_name: "Carlos Rodriguez",
      email: "carlos@flnucs.com",
      phone: "(305) 555-0126",
      address: "321 Orange Blossom Trail",
      city: "Orlando",
      state: "FL",
      latitude: 28.5383,
      longitude: -81.3792,
      business_type: "Nuc Producer",
      profile_type: "premium",
      verification_status: "approved",
      rating: 4.7,
      review_count: 156,
      image_url: "/api/placeholder/300/200",
      specialties: ["5-Frame Nucs", "Starter Colonies", "Package Bees"],
      inventory_status: "Pre-Order",
      website: "https://flnucs.com",
      description: "Specializing in strong, healthy nucleus colonies ready for Florida's unique climate conditions."
    }
  ];

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      setSellers(mockSellers);
      setLoading(false);
    }, 1000);
  }, []);

  const businessTypes = [
    'Honey Producer',
    'Queen Breeder',
    'Equipment Supplier',
    'Nuc Producer',
    'Pollination Services',
    'Bee Removal'
  ];

  const states = ['AL', 'AR', 'AZ', 'FL', 'GA', 'KY', 'LA', 'MS', 'MO', 'NC', 'NM', 'OK', 'SC', 'TN', 'TX', 'VA', 'WV'];

  const filteredSellers = sellers.filter(seller => {
    if (filters.search && !seller.business_name.toLowerCase().includes(filters.search.toLowerCase()) &&
        !seller.city.toLowerCase().includes(filters.search.toLowerCase()) &&
        !seller.specialties.some(s => s.toLowerCase().includes(filters.search.toLowerCase()))) {
      return false;
    }
    if (filters.businessType && seller.business_type !== filters.businessType) {
      return false;
    }
    if (filters.state && seller.state !== filters.state) {
      return false;
    }
    if (filters.profileType && seller.profile_type !== filters.profileType) {
      return false;
    }
    if (filters.verificationStatus && seller.verification_status !== filters.verificationStatus) {
      return false;
    }
    return true;
  });

  const displayedSellers = limit ? filteredSellers.slice(0, limit) : filteredSellers;

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <span key={i}>
        {i < Math.floor(rating) ? (
          <StarIconSolid className="h-4 w-4 text-yellow-400" />
        ) : (
          <StarIcon className="h-4 w-4 text-gray-300" />
        )}
      </span>
    ));
  };

  const SellerCard: React.FC<{ seller: Seller }> = ({ seller }) => (
    <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
      <div className="relative">
        <img
          src={seller.image_url || "/api/placeholder/300/200"}
          alt={seller.business_name}
          className="w-full h-48 object-cover"
        />
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            seller.verification_status === 'approved' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-yellow-100 text-yellow-800'
          }`}>
            {seller.verification_status === 'approved' ? '✓ Verified' : '⏳ Pending'}
          </span>
        </div>
        {seller.profile_type === 'premium' && (
          <div className="absolute top-2 left-2">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              ⭐ Premium
            </span>
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-lg font-semibold text-gray-900">{seller.business_name}</h3>
          <div className="flex items-center space-x-1">
            {renderStars(seller.rating)}
            <span className="text-sm text-gray-600 ml-1">({seller.review_count})</span>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 mb-2">{seller.business_type}</p>
        
        <div className="flex items-center text-sm text-gray-500 mb-2">
          <MapPinIcon className="h-4 w-4 mr-1" />
          {seller.city}, {seller.state}
        </div>
        
        {seller.description && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">{seller.description}</p>
        )}
        
        <div className="flex flex-wrap gap-1 mb-3">
          {seller.specialties.slice(0, 3).map((specialty, index) => (
            <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {specialty}
            </span>
          ))}
          {seller.specialties.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
              +{seller.specialties.length - 3} more
            </span>
          )}
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              seller.inventory_status === 'In Stock' 
                ? 'bg-green-100 text-green-800'
                : seller.inventory_status === 'Limited Stock'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {seller.inventory_status}
            </span>
          </div>
          
          <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
            View Details
          </button>
        </div>
      </div>
    </div>
  );

  const SellerListItem: React.FC<{ seller: Seller }> = ({ seller }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow duration-200">
      <div className="flex items-start space-x-4">
        <img
          src={seller.image_url || "/api/placeholder/100/100"}
          alt={seller.business_name}
          className="w-20 h-20 object-cover rounded-lg"
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{seller.business_name}</h3>
              <p className="text-sm text-gray-600">{seller.business_type}</p>
              <div className="flex items-center text-sm text-gray-500 mt-1">
                <MapPinIcon className="h-4 w-4 mr-1" />
                {seller.city}, {seller.state}
              </div>
            </div>
            
            <div className="flex flex-col items-end space-y-2">
              <div className="flex items-center space-x-1">
                {renderStars(seller.rating)}
                <span className="text-sm text-gray-600 ml-1">({seller.review_count})</span>
              </div>
              
              <div className="flex space-x-2">
                {seller.profile_type === 'premium' && (
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    ⭐ Premium
                  </span>
                )}
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  seller.verification_status === 'approved' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {seller.verification_status === 'approved' ? '✓ Verified' : '⏳ Pending'}
                </span>
              </div>
            </div>
          </div>
          
          {seller.description && (
            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{seller.description}</p>
          )}
          
          <div className="flex justify-between items-center">
            <div className="flex flex-wrap gap-1">
              {seller.specialties.slice(0, 4).map((specialty, index) => (
                <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                  {specialty}
                </span>
              ))}
            </div>
            
            <div className="flex items-center space-x-3">
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                seller.inventory_status === 'In Stock' 
                  ? 'bg-green-100 text-green-800'
                  : seller.inventory_status === 'Limited Stock'
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {seller.inventory_status}
              </span>
              
              <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
                View Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-64 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">{title}</h2>
        
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by business name, city, or specialty..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <select
                value={filters.businessType}
                onChange={(e) => setFilters({ ...filters, businessType: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Business Types</option>
                {businessTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              
              <select
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All States</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              
              <select
                value={filters.verificationStatus}
                onChange={(e) => setFilters({ ...filters, verificationStatus: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Status</option>
                <option value="approved">✓ Verified Only</option>
                <option value="pending">⏳ Pending Only</option>
              </select>
            </div>
            
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Showing {displayedSellers.length} of {filteredSellers.length} beekeepers
              </p>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <ViewColumnsIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <ListBulletIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {displayedSellers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <MagnifyingGlassIcon className="h-16 w-16 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sellers found</h3>
          <p className="text-gray-600">Try adjusting your search criteria or filters.</p>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedSellers.map(seller => (
            <SellerCard key={seller.seller_id} seller={seller} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {displayedSellers.map(seller => (
            <SellerListItem key={seller.seller_id} seller={seller} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EnhancedSellerDirectory;
